/**
 * useTeamRosterImage
 *
 * Resolves a team's CURRENT canonical roster image for view-only display on
 * web. Annotations are authored on iPad as separate notes; web just shows the
 * roster image (not a saved annotation snapshot).
 *
 * The resolution has four client states [SpecFlow I3]:
 *  - `loading`  — request in flight
 *  - `resolved` — a roster image is available
 *  - `empty`    — the team has no roster image yet (API 404). Callers should
 *                 surface an upload CTA, not an error.
 *  - `error`    — sign failure / network / expired URL. Callers offer a retry;
 *                 we never hand back a broken image.
 *
 * Signed URLs are short-lived. We re-fetch on `<img>` load failure (one retry)
 * so an expired URL recovers transparently instead of rendering broken.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getTeamRosterImage } from '../services/teams';
import { ApiError } from '../services/api';

export type TeamRosterImageStatus = 'loading' | 'resolved' | 'empty' | 'error';

interface UseTeamRosterImageResult {
  status: TeamRosterImageStatus;
  /** The current roster image URL. `null` unless `status === 'resolved'`. */
  imageUrl: string | null;
  errorMessage: string | null;
  /** Re-run resolution (used by retry and `<img onError>` recovery). */
  retry: () => void;
}

/**
 * A `null`/empty `teamId` keeps the hook idle without firing a request. The
 * idle state is reported as `status: 'empty'` for simplicity, so consumers MUST
 * gate their own "no roster / upload CTA" rendering on having a team selected
 * (don't show it purely off `status === 'empty'`). `RosterImageSection` does
 * this by checking `selectedTeam` before inspecting `status`.
 */
export function useTeamRosterImage(
  teamId: string | null,
): UseTeamRosterImageResult {
  const [status, setStatus] = useState<TeamRosterImageStatus>('empty');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Bumped to force a re-resolve (retry / onError recovery) without changing teamId.
  const [reloadToken, setReloadToken] = useState(0);

  // Guards against a slow resolution for a previous team (or pre-retry request)
  // clobbering state after the selection changed.
  const requestIdRef = useRef(0);

  const retry = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!teamId) {
      setStatus('empty');
      setImageUrl(null);
      setErrorMessage(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;
    const isStale = () => cancelled || requestIdRef.current !== requestId;

    const resolve = async () => {
      setStatus('loading');
      setErrorMessage(null);
      // Clear the prior result so `imageUrl` is never read as a stale value
      // while `status === 'loading'`.
      setImageUrl(null);

      // Resolve the canonical roster image. A 404 here is the defined "empty"
      // state (no roster yet / object gone) — not an error.
      try {
        const roster = await getTeamRosterImage(teamId);
        if (isStale()) return;
        setImageUrl(roster.signedUrl);
        setStatus('resolved');
      } catch (err) {
        if (isStale()) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setStatus('empty');
          setImageUrl(null);
          return;
        }
        console.error('[useTeamRosterImage] Failed to resolve roster image', {
          error: err,
          teamId,
        });
        setStatus('error');
        setImageUrl(null);
        setErrorMessage('Could not load this team’s roster image. Try again.');
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [teamId, reloadToken]);

  return {
    status,
    imageUrl,
    errorMessage,
    retry,
  };
}
